import type { BattleCondition, BattleOp } from 'features/battle/types';
import { effectsForBadge, isTowerBadge } from 'features/catalog/badge-definitions';

/** Catalog pills. An empty selection shows every badge; a few selected badges match any of them. */
export const BATTLE_EFFECT_FILTERS = [
  'prestige',
  'draw',
  'reveal',
  'activate',
  'return',
  'replace',
  'boost',
  'tower',
] as const;

export type BattleEffectFilter = (typeof BATTLE_EFFECT_FILTERS)[number];

export const BATTLE_EFFECT_FILTER_LABELS: Record<BattleEffectFilter, string> = {
  prestige: 'Prestige',
  draw: 'Draw',
  reveal: 'On Reveal',
  activate: 'Activate',
  return: 'Return',
  replace: 'Replace',
  boost: 'Boost',
  tower: 'No Effect',
};

const tag = (into: Set<BattleEffectFilter>, ...filters: BattleEffectFilter[]) => {
  for (const filter of filters) into.add(filter);
};

const tagCondition = (condition: BattleCondition, into: Set<BattleEffectFilter>) => {
  if (condition.kind === 'prestige' || condition.kind === 'prestige_at_most') tag(into, 'prestige');
  else if (
    condition.kind === 'drawn_rarity' ||
    condition.kind === 'drawn_matches_source_rarity' ||
    condition.kind === 'drawn_matches_filter' ||
    condition.kind === 'only_self_drawn_matches_filter' ||
    condition.kind === 'drawn_rarity_unrepresented' ||
    condition.kind === 'drawn_rarity_represented' ||
    condition.kind === 'drawn_rarity_matches_opponent_board_badge' ||
    condition.kind === 'last_two_drawn_same_rarity' ||
    condition.kind === 'last_two_drawn_share_stat' ||
    condition.kind === 'last_two_drawn_match_filter' ||
    condition.kind === 'drawn_rarities_match'
  )
    tag(into, 'draw');
  else if (condition.kind === 'returned_own_badge') tag(into, 'return');
};

const tagOp = (op: BattleOp, into: Set<BattleEffectFilter>) => {
  if (
    op.kind === 'gain_prestige' ||
    op.kind === 'steal_prestige' ||
    op.kind === 'lose_prestige' ||
    op.kind === 'shield_prestige' ||
    op.kind === 'gain_prestige_per_drawn' ||
    op.kind === 'neighbor_activate_prestige'
  ) {
    tag(into, 'prestige');
    if (op.kind === 'gain_prestige_per_drawn') tag(into, 'draw');
    return;
  }
  if (
    op.kind === 'discard' ||
    op.kind === 'discard_drawn' ||
    op.kind === 'discard_deck' ||
    op.kind === 'discard_named_rarity' ||
    op.kind === 'discard_neighbor' ||
    op.kind === 'discard_board' ||
    op.kind === 'discard_board_badges' ||
    op.kind === 'discard_revealed' ||
    op.kind === 'discard_self_board_badge' ||
    op.kind === 'discard_source'
  ) {
    if (op.kind === 'discard_drawn') tag(into, 'draw');
    return;
  }
  if (
    op.kind === 'draw' ||
    op.kind === 'draw_up_to' ||
    op.kind === 'draw_up_to_tied' ||
    op.kind === 'draw_rarity' ||
    op.kind === 'scry_draw' ||
    op.kind === 'cycle_hand'
  ) {
    tag(into, 'draw');
    return;
  }
  if (
    op.kind === 'reveal_if' ||
    op.kind === 'reveal_hand' ||
    op.kind === 'reveal_hand_neighbor' ||
    op.kind === 'reveal_drawn' ||
    op.kind === 'reveal_unrepresented_drawn' ||
    op.kind === 'reveal_unique_filter_drawn' ||
    op.kind === 'reveal_from_decks'
  ) {
    if ('play' in op && op.play) tag(into, 'activate');
    if (
      op.kind === 'reveal_drawn' ||
      op.kind === 'reveal_unrepresented_drawn' ||
      op.kind === 'reveal_unique_filter_drawn' ||
      op.kind === 'reveal_from_decks'
    )
      tag(into, 'draw');
    return;
  }
  if (op.kind === 'play_other' || op.kind === 'ignore_stat_lead' || op.kind === 'activate_on_reveal') {
    tag(into, 'activate');
    return;
  }
  if (
    op.kind === 'bounce_opponent_board_badge' ||
    op.kind === 'bounce_self_board_badge' ||
    op.kind === 'bounce_source' ||
    op.kind === 'bounce_opponent_matching_drawn'
  ) {
    tag(into, 'return');
    if (op.kind === 'bounce_opponent_matching_drawn') tag(into, 'draw');
    return;
  }
  if (
    op.kind === 'swap_source_with_opponent_board' ||
    op.kind === 'swap_random_hand' ||
    op.kind === 'swap_hand' ||
    op.kind === 'exchange_self_with_opponent_hand' ||
    op.kind === 'offer_swap_drawn' ||
    op.kind === 'replace_self_from_deck' ||
    op.kind === 'replace_neighbor_from_deck' ||
    op.kind === 'replace_other_piles' ||
    op.kind === 'upgrade_pile_to_random'
  ) {
    tag(into, 'replace');
    if (op.kind === 'offer_swap_drawn') tag(into, 'draw');
    if ('play' in op && op.play) tag(into, 'activate');
    return;
  }
  if (op.kind === 'gain_stat' || op.kind === 'opposite_gain_stat' || op.kind === 'boost_stat') {
    tag(into, 'boost');
    return;
  }
  if (op.kind === 'guess_drawn_rarity') {
    tag(into, 'draw');
    if (op.reward === 'prestige') tag(into, 'prestige');
    return;
  }
  if (op.kind === 'if') {
    tagCondition(op.condition, into);
    tagOps(op.then, into);
    tagOps(op.otherwise ?? [], into);
  }
};

const tagOps = (ops: readonly BattleOp[], into: Set<BattleEffectFilter>) => {
  for (const op of ops) tagOp(op, into);
};

/** Effect categories this op list actually performs, including steps nested under If. */
export const effectFiltersForOps = (ops: readonly BattleOp[]): ReadonlySet<BattleEffectFilter> => {
  const into = new Set<BattleEffectFilter>();
  tagOps(ops, into);
  return into;
};

export const effectFiltersForBadge = (badgeId: string): ReadonlySet<BattleEffectFilter> => {
  const effects = effectsForBadge(badgeId);
  const into = new Set(effectFiltersForOps(effects.flatMap((effect) => effect.ops)));
  if (effects.some((effect) => effect.trigger === 'On Reveal')) into.add('reveal');
  if (isTowerBadge(badgeId)) into.add('tower');
  return into;
};
